import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { spacesAPI, campusAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { ArrowLeft, Save, Upload, X } from "lucide-react";
import { Space, Campus, CreateSpaceDto, UpdateSpaceDto } from "../types";
import { api } from "@/api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useAuthStore } from "../store/authStore";

export function SpaceFormPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isEditing = spaceId && spaceId !== 'new';
  const isAdmin = user?.role === '7891';

  const [formData, setFormData] = useState<CreateSpaceDto & { is_active?: boolean; category?: string; contactEmail?: string; memberCountRange?: string }>({
    name: '',
    description: '',
    campus_id: user?.campus_id || '',
    image_url: '',
    category: '',
    contactEmail: user?.email || '',
    memberCountRange: '1-10',
    is_active: true,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [categories, setCategories] = useState<Array<{ _id: string; name: string }>>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const {
    data: spaceData,
    isLoading: isLoadingSpace,
    error: spaceError,
  } = useDataFetching([...QUERY_KEYS.SPACES, spaceId || ''], {
    queryFn: () => spacesAPI.getSpace(spaceId!),
    enabled: !!spaceId && !!isEditing,
  });

  const {
    data: campusesData,
    isLoading: isLoadingCampuses,
  } = useDataFetching(QUERY_KEYS.CAMPUSES, {
    queryFn: campusAPI.getAllCampuses,
  });

  const space = spaceData as Space;
  // Log to debug campus data structure
  console.log('Raw campusesData:', campusesData);

  // Handle different response structures
  let campuses: Campus[] = [];
  if (campusesData) {
    if (Array.isArray(campusesData)) {
      campuses = campusesData;
    } else if (campusesData.data && Array.isArray(campusesData.data)) {
      campuses = campusesData.data;
    } else if (campusesData.items && Array.isArray(campusesData.items)) {
      campuses = campusesData.items;
    } else if (campusesData.campuses && Array.isArray(campusesData.campuses)) {
      campuses = campusesData.campuses;
    }
  }

  // Filter campuses based on user role
  let availableCampuses = campuses;
  if (!isAdmin && user?.campus_id) {
    // Regular users can only see their own campus
    availableCampuses = campuses.filter((campus: Campus) => campus._id === user.campus_id);
  }

  console.log('Available campuses for user:', availableCampuses);

  // Fetch space categories
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await api.get('/space-categories');
        setCategories(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (space && isEditing) {
      setFormData({
        name: space.title || space.name || '',
        description: space.about || space.description || '',
        campus_id: typeof space.campusId === 'string' ? space.campusId : (typeof space.campus_id === 'string' ? space.campus_id : space.campusId?._id || space.campus_id?._id || ''),
        image_url: space.logoUrl || space.image_url || '',
        category: typeof space.category === 'string' ? space.category : space.category?._id || '',
        contactEmail: space.contactEmail || user?.email || '',
        memberCountRange: space.memberCountRange || '1-10',
        is_active: space.is_active !== false,
      });
      if (space.logoUrl || space.image_url) {
        setImagePreview(space.logoUrl || space.image_url || '');
      }
    }
  }, [space, isEditing]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({ ...formData, image_url: '' });
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Space name is required');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Space description is required');
      return false;
    }
    if (!formData.category) {
      setError('Category is required');
      return false;
    }
    if (!formData.campus_id) {
      setError('Campus is required');
      return false;
    }
    if (!formData.contactEmail || !formData.contactEmail.trim()) {
      setError('Contact email is required');
      return false;
    }
    if (!formData.memberCountRange) {
      setError('Member count range is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let imageUrl = formData.image_url;

      // Upload image if a new file was selected
      if (imageFile && isEditing) {
        const uploadResponse = await spacesAPI.uploadSpaceImage(spaceId!, imageFile);
        imageUrl = uploadResponse.data.image_url;
      }

      if (isEditing) {
        const updateData: UpdateSpaceDto = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          contactEmail: formData.contactEmail,
          memberCountRange: formData.memberCountRange,
          image_url: imageUrl,
          is_active: formData.is_active,
        };
        await spacesAPI.updateSpace(spaceId!, updateData);
      } else {
        const createData: CreateSpaceDto = {
          name: formData.name,
          description: formData.description,
          campus_id: formData.campus_id,
          category: formData.category,
          contactEmail: formData.contactEmail,
          memberCountRange: formData.memberCountRange,
          image_url: imageUrl,
        };
        const response = await spacesAPI.createSpace(createData);
        
        // Upload image after creating the space
        if (imageFile && response.data._id) {
          await spacesAPI.uploadSpaceImage(response.data._id, imageFile);
        }
      }

      navigate('/spaces');
    } catch (err) {
      const error = err as Error & { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to save space');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingSpace && isEditing) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (spaceError && isEditing) return <DashboardLayout><ErrorState message={spaceError.message} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(isEditing ? `/spaces/${spaceId}` : '/spaces')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span>{isEditing ? 'Edit Space' : 'Create New Space'}</span>
          </div>
        }
      />

      <form onSubmit={handleSubmit} className="mt-6 max-w-4xl">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Enter the basic details for your space
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Space Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Computer Science Club"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this space is about..."
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="campus">Campus *</Label>
                  <Select
                    value={formData.campus_id}
                    onValueChange={(value) => setFormData({ ...formData, campus_id: value })}
                    disabled={isEditing || isLoadingCampuses || (!isAdmin && !isEditing)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a campus" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCampuses.map((campus: Campus) => (
                        <SelectItem key={campus._id} value={campus._id}>
                          {campus.campus_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    disabled={loadingCategories}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category._id} value={category._id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="e.g., club@university.edu"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="memberCountRange">Member Count Range *</Label>
                  <Select
                    value={formData.memberCountRange}
                    onValueChange={(value) => setFormData({ ...formData, memberCountRange: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select member range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 members</SelectItem>
                      <SelectItem value="11-25">11-25 members</SelectItem>
                      <SelectItem value="26-50">26-50 members</SelectItem>
                      <SelectItem value="51-100">51-100 members</SelectItem>
                      <SelectItem value="101-250">101-250 members</SelectItem>
                      <SelectItem value="251-500">251-500 members</SelectItem>
                      <SelectItem value="500+">500+ members</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {isEditing && (
                  <div className="space-y-2">
                    <Label htmlFor="active">Active Status</Label>
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        id="active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                      <Label htmlFor="active" className="cursor-pointer">
                        {formData.is_active ? 'Active' : 'Inactive'}
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Space Image</CardTitle>
              <CardDescription>
                Upload an image to represent your space
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Space preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}

                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="cursor-pointer"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEditing ? `/spaces/${spaceId}` : '/spaces')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Update Space' : 'Create Space'}
              </>
            )}
          </Button>
        </div>
      </form>
    </DashboardLayout>
  );
}
